import { useEffect, useRef } from "react";
import { useAuth } from "react-oidc-context";
import { useQueryClient } from "@tanstack/react-query";
import { wsEndpoint } from "@/config";
import { useAccount } from "@/contexts/AccountContext";
import type { Infringement } from "@/types/domain";

const BACKOFF_INITIAL_MS = 1000;
const BACKOFF_MAX_MS = 30000;

/* Opens a WebSocket connection to the infringements stream for the current */
export function useInfringementsWebSocket(): void {
  const { user } = useAuth();
  const { tenantId } = useAccount();
  const queryClient = useQueryClient();
  const wsRef = useRef<WebSocket | null>(null);
  const backoffRef = useRef(BACKOFF_INITIAL_MS);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const token = user?.access_token;

  useEffect(() => {
    if (!token || !tenantId) return;

    let cancelled = false;

    function clearReconnectTimer() {
      if (reconnectTimerRef.current !== null) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
    }

    function connect() {
      if (cancelled) return;

      const url = `${wsEndpoint}?token=${encodeURIComponent(token!)}`;
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        if (cancelled) {
          ws.close();
          return;
        }
        backoffRef.current = BACKOFF_INITIAL_MS;
        ws.send(JSON.stringify({ action: "subscribe", tenantId }));
      };

      ws.onmessage = (event: MessageEvent) => {
        if (cancelled) return;
        let infringement: Infringement;
        try {
          infringement = JSON.parse(event.data as string) as Infringement;
        } catch {
          return;
        }
        if (!infringement?.id) return;

        // Update the infringement list in all relevant queries
        queryClient.setQueriesData<{ infringements: Infringement[]; count: number }>(
          { queryKey: ["infringements"] },
          (prev) => {
            if (!prev) return prev;
            const existing = prev.infringements.findIndex((i) => i.id === infringement.id);
            if (existing !== -1) {
              // Update in place
              const updated = [...prev.infringements];
              updated[existing] = infringement;
              return { ...prev, infringements: updated };
            }
            // New infringement
            return {
              infringements: [infringement, ...prev.infringements],
              count: prev.count + 1,
            };
          },
        );
      };

      ws.onclose = () => {
        if (cancelled) return;
        scheduleReconnect();
      };

      ws.onerror = () => {
        ws.close();
      };
    }

    function scheduleReconnect() {
      clearReconnectTimer();
      const delay = backoffRef.current;
      backoffRef.current = Math.min(backoffRef.current * 2, BACKOFF_MAX_MS);
      reconnectTimerRef.current = setTimeout(connect, delay);
    }

    connect();

    return () => {
      cancelled = true;
      clearReconnectTimer();
      const ws = wsRef.current;
      if (ws) {
        // Prevent console error in strict mode if closed while connecting
        if (ws.readyState === WebSocket.CONNECTING) {
          ws.onopen = () => ws.close();
        } else {
          ws.close();
        }
      }
      wsRef.current = null;
    };
  }, [token, tenantId, queryClient]);
}
